<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo auth-logo"
          alt="Continuum Design"
          src="/continuum-design-logo.svg"
        />
        <h3 class="text-title">Sign up with your email address.</h3>
      </div>

      <div class="auth-card-container">
        <v-form ref="form" v-model="valid">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              autofocus
              name="firstname"
              validate-on="input lazy"
              v-model="firstname"
              :maxLength="40"
              hint="At most 40 characters"
              :rules="[rules.required]"
              variant="outlined"
              density="default"
              label="First Name *"
            >
            </v-text-field>

            <v-text-field
              name="lastname"
              validate-on="input lazy"
              v-model="lastname"
              :maxLength="40"
              hint="At most 40 characters"
              :rules="[rules.required]"
              variant="outlined"
              density="default"
              label='Last Name *'
            >
            </v-text-field>

            <v-text-field
              name="email"
              label="Email *"
              type="email"
              autocomplete="none"
              validate-on="input lazy"
              :maxLength="254"
              v-model="email"
              :rules="[rules.required, rules.email]"
              variant="outlined"
              density="default"
            >
            </v-text-field>

            <v-text-field
              id="password"
              name="password"
              class="password-field"
              autocomplete="new-password"
              counter
              v-model="password"
              :maxLength="254"
              :append-inner-icon="e1 ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="() => (e1 = !e1)"
              :type="e1 ? 'password' : 'text'"
              :rules="[rules.required, rules.passwordPolicy]"
              validate-on="input lazy"
              min="8"
              label="Password *"
              :hint="passwordHint"
              variant="outlined"
              density="default"
            >
            </v-text-field>

            <v-textarea
              name="referralSource"
              label="How did you hear about us?"
              validate-on="input lazy"
              v-model="referralSource"
              :maxLength="100"
              :rules="[rules.minCharacters]"
              variant="outlined"
              density="default"
              hint="At most 100 characters"
            >
            </v-textarea>

            <div class="d-flex align-center" v-if="paywallEnabled && !isEnterpriseSignup">
              <img
                class="bullet-checked"
                alt="Continuum Design"
                src="@/assets/compare-checked.svg"
              />
              <v-label class="text-wrap">
                <span>No credit card is required. Get the Pro Plan free for 4 weeks. Cancel anytime.</span>
              </v-label>
            </div>
            <v-checkbox
              v-model="checkbox"
              :rules="[rules.terms]"
              class="d-inline-block mt-0"
            >
              <template v-slot:input>
                <div @click="checkbox = !checkbox" class="d-flex align-center">
                  <v-icon v-if="!checkbox">
                    mdi-checkbox-blank-outline
                  </v-icon>
                  <img
                    v-else
                    class="bullet-checked"
                    alt="Continuum Design"
                    src="@/assets/compare-checked.svg"
                  />
                </div>
              </template>
              <template v-slot:label>
                <div>
                  <span @click="checkbox = !checkbox">I agree to the </span>
                  <strong
                  ><u
                  ><a
                    onclick="window.open('../../Continuum_Design_terms_of_service_and_privacy_policy.pdf','_blank');"
                    href="#"
                  >Terms of Service & Privacy Policy</a
                  ></u
                  ></strong
                  >
                </div>
              </template>
            </v-checkbox>

            <p class="message bg-error" v-if="error">{{ error }}</p>
            <p class="message bg-indigo" v-if="message">{{ message }}</p>
          </div>

          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <p class="text">
                  Already have an account?
                  <router-link
                    class="text text-decoration-none"
                    tabindex="5"
                    :to="{ path: '/login' }"
                  >
                    <span class="light--text">Log in</span>
                  </router-link>
                </p>
              </div>
              <div class="auth-card-footer-part">
                <v-btn rounded="lg" size="large" color="primary" class="ml-auto d-flex" variant="flat" @click="submit"
                       tabindex="3" :disabled="!checkbox || submitting" :loading="submitting">
                  Create account
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
        <div class="auth-card-container">
          <text-divider text="or" class="mr-n2"/>
          <google-sign-in-button isForSignUp :disabled="checkbox === false"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import GoogleSignInButton from '@/components/GoogleSignInButton.vue'
import TextDivider from '@/components/TextDivider.vue'
import { passwordValidationRule, PASSWORD_HINT, isValidPassword } from '@/helpers/passwordValidation'
import { authProxyApi } from '@/api/authProxyApi'
import { isPaywallEnabled } from '@/utils/featureFlags'

const route = useRoute()
const router = useRouter()

const form = ref(null)
const valid = ref(true)
const checkbox = ref(false)
const e1 = ref(true)
const submitting = ref(false)
const error = ref(null)
const message = ref(null)
const firstname = ref('')
const lastname = ref('')
const email = ref('')
const password = ref('')
const referralSource = ref('')

const rules = {
  required: (value) => !!value || 'Required',
  email: (value) => {
    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

    return pattern.test(value) || 'Invalid e-mail'
  },
  terms: (value) => value || 'You must agree to continue!',
  minCharacters: (value) => !value || value.length >= 10 || 'Please enter at least 10 characters',
  passwordPolicy: passwordValidationRule
}

const paywallEnabled = computed(() => isPaywallEnabled())

// Enterprise members arrive via the welcome email's ?plan=enterprise link; the Pro-plan
// card notice doesn't apply to them (their seat is paid by the organisation).
const isEnterpriseSignup = computed(() => route.query.plan === 'enterprise')

const passwordHint = computed(() => isValidPassword(password.value) ? '' : PASSWORD_HINT)

const submit = async () => {
  if (submitting.value) {
    return
  }
  submitting.value = true
  error.value = null
  message.value = null
  try {
    const { valid: isValid } = await form.value.validate()

    if (!isValid) {
      error.value = 'One or more fields are invalid.'

      return
    }

    // Maintain a minimum delay to keep timing roughly consistent across
    // outcomes. Account existence is disclosed via response code for
    // EMAIL_TAKEN by design (matches Google/Microsoft signup UX), but we
    // still avoid leaking timing on unrelated transient errors.
    const start = Date.now()
    const minDelayMs = 800

    const normalizedEmail = (email.value || '').toLowerCase()
    email.value = normalizedEmail

    let response = null
    try {
      response = await authProxyApi.signup({
        email: normalizedEmail,
        password: password.value,
        firstname: firstname.value,
        lastname: lastname.value,
        referralSource: referralSource.value
      })
    } catch (e) {
      // Network/transport errors: keep generic to avoid leaking timing/state.
      console.error('Signup via auth proxy failed:', e)
    }

    const remaining = minDelayMs - (Date.now() - start)
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining))
    }

    // Coded failures from the backend (EMAIL_TAKEN, SIGNUP_FAILED,
    // INVALID_PASSWORD, INVALID_INPUT). Render the backend message in-place
    // and stay on the form. EMAIL_TAKEN's message already directs the user
    // to log in or reset password, so the existing footer links cover the
    // recovery actions — no need for a dedicated alert.
    if (response?.ok === false) {
      error.value = response.message || 'We couldn\'t complete your sign-up. Please try again.'

      return
    }

    // Success path (or no response from a network/transport error):
    // redirect to /confirm so a legitimate signup can complete with
    // the emailed code.
    await router.replace({
      path: '/confirm',
      query: { username: normalizedEmail, projectId: route.query.projectId, redirect: route.query.redirect }
    })
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  window.trackSignupPageView()
})
</script>

<style scoped>
.text {
  color: var(--text-dark);
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 180px;
  margin-bottom: 24px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.bullet-checked {
  width: 30px;
  height: 30px;
  margin: 0 5px 0 5px;
}

[data-theme="dark"] a,
[data-theme="dark"] a .light--text {
  color: var(--blue-1);
}

.password-field :deep(.v-input__details) {
  min-height: 40px;
  align-items: flex-start;
}

</style>
